import { doc, setDoc, getDoc, collection, getDocs, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { validateWorkout, normalizeLegacy, unitForScoreType } from "../schemas/workout";

class Workout {
  static _normalize(data) {
    const d = normalizeLegacy({
      ...data,
      name: data.name ?? '',
      description: data.description ?? '',
      exercises: Array.isArray(data.exercises) ? data.exercises : [],
    });
    // Derived field for backward compat with pages that read measurementUnit
    d.measurementUnit = unitForScoreType(d.scoreType);
    return d;
  }

  static makeWebSafeId(name) {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .substring(0, 50);
  }

  static getCollectionPath(userId) {
    return `workouts/${userId}/definitions`;
  }

  static getDocPath(userId, workoutId) {
    return `workouts/${userId}/definitions/${workoutId}`;
  }

  // Create a new workout definition
  static async create(userId, data) {
    const validation = validateWorkout(data);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    const workoutId = Workout.makeWebSafeId(data.name);
    const workoutRef = doc(db, Workout.getDocPath(userId, workoutId));

    await setDoc(workoutRef, {
      ...validation.data,
      createdAt: Timestamp.now(),
    });
    return workoutId;
  }

  // Get a single workout by ID (normalizes legacy docs on read)
  static async getById(userId, workoutId) {
    const workoutRef = doc(db, Workout.getDocPath(userId, workoutId));
    const workoutDoc = await getDoc(workoutRef);

    if (!workoutDoc.exists()) {
      return null;
    }

    return Workout._normalize({ id: workoutDoc.id, ...workoutDoc.data() });
  }

  // Get all workouts for a user
  static async getAll(userId) {
    const workoutsRef = collection(db, Workout.getCollectionPath(userId));
    const snapshot = await getDocs(workoutsRef);

    return snapshot.docs.map(doc =>
      Workout._normalize({ id: doc.id, ...doc.data() })
    );
  }

  // Update workout
  static async update(userId, workoutId, data) {
    const validation = validateWorkout(data);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    const workoutRef = doc(db, Workout.getDocPath(userId, workoutId));
    await updateDoc(workoutRef, {
      ...validation.data,
      updatedAt: Timestamp.now(),
    });
  }

  // Delete workout
  static async delete(userId, workoutId) {
    const workoutRef = doc(db, Workout.getDocPath(userId, workoutId));
    await deleteDoc(workoutRef);
  }

  // Copy a global workout into the user's collection
  static async copyFromGlobal(userId, globalWorkoutId, globalWorkoutData) {
    const workoutId = Workout.makeWebSafeId(globalWorkoutData.name || globalWorkoutId);
    const workoutRef = doc(db, Workout.getDocPath(userId, workoutId));

    // Only copy schema-relevant fields
    const { id, isGlobal, measurementUnit, ...fields } = globalWorkoutData;
    await setDoc(workoutRef, {
      ...fields,
      copiedFromGlobal: globalWorkoutId,
      createdAt: Timestamp.now(),
    });
    return workoutId;
  }
}

export default Workout;
